import Dexie, { Table } from 'dexie';
import { BehaviorSubject } from 'rxjs';
import { PendingAnswers } from '../models/pending-answers';
import { TemporaryAnswers } from '../models/temporary-answers';
import { StorageRecord } from '../models/storage-record';
import { DurationAuditRecord } from '../models/duration-validation';

const DB_NAME = 'ngdexieliveQuery';
const DB_VERSION = 9;
const DB_VERSION_STORAGE_KEY = `${DB_NAME}_schema_version`;

export class AppDB extends Dexie {
    pendingAnswers!: Table<PendingAnswers>
    temporaryAnswers!: Table<TemporaryAnswers>
    storageRecords!: Table<StorageRecord>
    durationAudits!: Table<DurationAuditRecord>

    db_open: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private initPromise: Promise<void> | null = null;

    constructor() {
        super(DB_NAME);
        this.version(DB_VERSION).stores({
            // pending answers means: answers saved in offline mode ("pending" to be POSTED)
            pendingAnswers: '++id, su_survey_id, qs_sensor_id, qs_campaign_id, ip, user_agent, answers, extra_data, respondent_data, created_at, status, expiresAt',
            // temporary answers means: answers saved while answering a survey, can later be processed as partial answers in QServus
            temporaryAnswers: '++id, survey_uuid, respondent_uuid, answers, created_at, expiresAt',
            // storage records means: key-value pairs previously stored in localStorage, now with expiration support
            storageRecords: '++id, &key, expiresAt, createdAt',
            // duration audits store rejected submissions metadata
            durationAudits: '++id, su_survey_id, qs_company_id, reason, created_at, synced, expiresAt'
        });
    }

    ensureInitialized(): Promise<void> {
        if (this.isOpen()) {
            return Promise.resolve();
        }

        if (!this.initPromise) {
            this.initPromise = ensureDatabaseVersion()
                .catch((error) => {
                    console.warn('Database version precheck failed:', error);
                })
                .then(() => this.openWithRecovery())
                .finally(() => {
                    this.initPromise = null;
                });
        }

        return this.initPromise;
    }

    private async openWithRecovery(alreadyRetried: boolean = false): Promise<void> {
        try {
            await this.open();
            this.persistCurrentVersion();
            this.db_open.next(true);
        } catch (error) {
            this.db_open.next(false);
            console.warn('Dexie failed to open IndexedDB:', error);

            if (alreadyRetried || !hasIndexedDBSupport()) {
                return;
            }

            try {
                await deleteExistingDatabase(DB_NAME);
                removeStoredDatabaseVersion();
            } catch (resetError) {
                console.warn('Failed to delete IndexedDB during recovery attempt:', resetError);
                return;
            }

            await this.openWithRecovery(true);
        }
    }

    private persistCurrentVersion(): void {
        if (!hasLocalStorageSupport()) {
            return;
        }

        try {
            localStorage.setItem(DB_VERSION_STORAGE_KEY, DB_VERSION.toString());
        } catch (error) {
            console.warn('Failed to persist IndexedDB schema version metadata:', error);
        }
    }
    /**
     * Get a register by id in any table
     * @param table used to call get method
     * @param id primary key of the object to get
     * @returns Object found
     * @author Daniel García Silva
     * @date 2022-11-30
     */
    async _get(table: string, id: number) {
        if (db.isOpen()) {
            return await db.table(table).get(id);
        } else return null
    }
    /**
     * Get registers found by any key in any table
     * @param table used to call get method
     * @param key property name use in where clausule
     * @param value value to filter
     * @returns List of objects found
     * @author Daniel García Silva
     * @date 2022-11-30
     */
    async _getByKey(table: string, key: string, value: string | number) {
        if (db.isOpen()) {
            return db.table(table).where(key).equals(value).toArray();
        } else return []
    }
    /**
     * Create a new register in any table
     * @param table used to call add method
     * @param obj object to add
     * @returns the identifier of the new record
     * @author Daniel García Silva
     * @date 2022-11-30
     */
    async _add(table: string, obj: PendingAnswers | TemporaryAnswers) {
        if (db.isOpen()) {
            return await db.table(table).add(obj);
        } else return null
    }
    /**
     * Update a register in any table
     * @param table used to call update method
     * @param id primary key of the object to update
     * @param obj object data
     * @returns the identifier of the updated record
     * @author Daniel García Silva
     * @date 2022-11-30
     */
    async _update(table: string, id: number, obj: PendingAnswers | TemporaryAnswers) {
        if (db.isOpen()) {
            return await db.table(table).update(id, obj);
        } else return null
    }
    /**
     * Delete a register in any table
     * @param table used to call delete method
     * @param id primary key of the object to delete
     * @returns undefined
     * @author Daniel García Silva
     * @date 2022-11-30
     */
    async _delete(table: string, id: number) {
        if (db.isOpen()) {
            return await db.table(table).delete(id);
        } else return null
    }
    /**
     * Delete registers found by any key in any table
     * @param table used to call delete method
     * @param key property name use in where clausule
     * @param value value to filter
     * @returns undefined
     * @author Daniel García Silva
     * @date 2022-11-30
     */
    async _deleteByKey(table: string, key: string, value: string | number) {
        if (db.isOpen()) {
            return await db.table(table).where(key).equals(value).delete();
        } else return null
    }
}

export const db = new AppDB();

function ensureDatabaseVersion(): Promise<void> {
    if (!hasIndexedDBSupport()) {
        return Promise.resolve();
    }

    const storedVersion = getStoredDatabaseVersion();

    if (storedVersion === null) {
        return Promise.resolve();
    }

    if (storedVersion === DB_VERSION) {
        return Promise.resolve();
    }

    console.warn(`IndexedDB version mismatch detected (stored v${storedVersion}, expected v${DB_VERSION}). Reinstalling database.`);
    return deleteExistingDatabase(DB_NAME)
        .then(() => removeStoredDatabaseVersion())
        .catch((error) => console.warn('Failed to verify IndexedDB version:', error));
}

function getStoredDatabaseVersion(): number | null {
    if (!hasLocalStorageSupport()) {
        return null;
    }

    try {
        const storedValue = localStorage.getItem(DB_VERSION_STORAGE_KEY);
        if (!storedValue) {
            return null;
        }
        const parsed = parseInt(storedValue, 10);
        return Number.isNaN(parsed) ? null : parsed;
    } catch (error) {
        console.warn('Failed to read stored IndexedDB version metadata:', error);
        return null;
    }
}

function deleteExistingDatabase(name: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!hasIndexedDBSupport()) {
            resolve();
            return;
        }

        const deleteRequest = indexedDB.deleteDatabase(name);

        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onblocked = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
    });
}

function removeStoredDatabaseVersion(): void {
    if (!hasLocalStorageSupport()) {
        return;
    }

    try {
        localStorage.removeItem(DB_VERSION_STORAGE_KEY);
    } catch (error) {
        console.warn('Failed to remove stored IndexedDB version metadata:', error);
    }
}

function hasIndexedDBSupport(): boolean {
    try {
        return typeof indexedDB !== 'undefined';
    } catch (error) {
        return false;
    }
}

function hasLocalStorageSupport(): boolean {
    try {
        return typeof localStorage !== 'undefined';
    } catch (error) {
        return false;
    }
}
